import { Dispatch, FC, ReactElement, useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import Modal from "web/Pages/Checkout/Subscription/Modal/modal";

import __ from "web/Layout/Translations";

import { IApiRequestMethods } from "web/api/apiRequestTypes";
import simpleApiRequest from "web/api/simpleApiRequest";

import { ModalActionTypes } from "web/hooks/useModal/useModalTypes";

import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";

import { SimpleFn } from "web/types/Common";
import {
  ISubscriptionActiveForm,
  ISubscriptionItemCurrentUserSubscription,
  ISubscriptionUserCurrent,
  SubscriptionActiveFormResend,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import { useAppContext } from "web/context/app";
import { setNotificationError } from "web/features/app/appSlice";
import { invalidateCurrentUserSubscription } from "web/features/subscriptions/subscriptionsApiSlice";

import EditEmail from "../../EditEmail";
import subscriptionActivitiesListStyle from "../../subscriptionActivitiesList.scss";
import ActivityItem, { ActivityItemProps } from "../activityItem";
import styles from "./emailSenderItem.scss";

type BenefitUUID = string;
type OnToggleEditModalHandler = (
  benefitUUID: BenefitUUID,
  activeForm: ISubscriptionActiveForm
) => void;

interface IEmailSenderActivityItemProps extends ActivityItemProps {
  handleToggleEditModal?: OnToggleEditModalHandler;
  onStartEditingEmail?: SimpleFn;
  setData: Dispatch<React.SetStateAction<ISubscriptionUserCurrent>>;
  benefitUUID: BenefitUUID;
  activeForm: ISubscriptionActiveForm;
  resendLink: string;
}

const modalTitle =
  "Edytuj adres e-mail, na który chcesz ponownie wysłać formularz.";

const classes = {
  root: subscriptionActivitiesListStyle.modalRoot,
  title: subscriptionActivitiesListStyle.modalTitle,
  content: subscriptionActivitiesListStyle.modalContent,
};

const EmailSenderActivityItem: FC<IEmailSenderActivityItemProps> = ({
  setData,
  benefitUUID,
  activeForm,
  resendLink,
  ...restProps
}) => {
  const dispatch = useDispatch();
  const [isEmailButtonActive, setIsEmailButtonActive] = useState(true);
  const [blockedButtonText, setBlockedButtonText] =
    useState<Nullable<string>>(null);

  const { modal } = useAppContext();
  const { dispatch: mDispatch } = modal;

  const setModal = (content: ReactElement) => {
    mDispatch({ type: ModalActionTypes.ADD_MODAL, modal: content });
  };

  const closeModal = useCallback(() => {
    mDispatch({ type: ModalActionTypes.RESET });
  }, [mDispatch]);

  const setChangedEmail = (
    newData: ISubscriptionItemCurrentUserSubscription
  ) => {
    setData((prevState) => {
      const newItems = prevState?.items?.map((item) => {
        const newItem = item["@id"] === newData["@id"] ? newData : item;
        return newItem;
      });
      return {
        ...prevState,
        items: newItems,
      };
    });
  };

  const onOpenEditEmailModalHandler = () => {
    setModal(
      <Modal title={__(modalTitle)} classes={classes}>
        <EditEmail
          activeForm={activeForm}
          benefitUUID={benefitUUID}
          setChangedEmail={setChangedEmail}
          closeModal={closeModal}
        />
      </Modal>
    );
  };

  const editEmailButton = (
    <button
      type="button"
      className={styles.editButton}
      onClick={onOpenEditEmailModalHandler}
    >
      {__("Edytuj adres e-mail")}
    </button>
  );

  const onResendEmailHandler = useCallback(() => {
    simpleApiRequest(resendLink, {
      method: IApiRequestMethods.POST,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    })
      .then((response) => {
        if (response.status === 425) {
          return response.json();
        }
        return null;
      })
      .then((data: SubscriptionActiveFormResend) => {
        if (data?.nextAvailableDate?.date) {
          const blockadeDate = new Date(data.nextAvailableDate.date);
          const unlockTime = new Date(
            blockadeDate.getTime() -
              blockadeDate.getTimezoneOffset() * 60 * 1000
          )
            .toLocaleString()
            .substr(12);
          const btnText = `${__("Ponowne wysłanie możliwe o")} ${unlockTime}`;
          setBlockedButtonText(btnText);
          setIsEmailButtonActive(false);

          const nextDateMessage = `${__(
            "Ponowne wysłanie e-maila będzie możliwe o"
          )} ${unlockTime}`;
          dispatch(setNotificationError({ message: nextDateMessage }));
          invalidateCurrentUserSubscription(dispatch);
        } else {
          setIsEmailButtonActive(false);
        }
      })
      .catch((error) => {
        newRelicErrorReport(
          error,
          "web-app/web/Pages/Customer/Content/Subscriptions/Activities/ActivityItem/activityItem.tsx - 79"
        );
        dispatch(setNotificationError({ message: __("Wystąpił błąd") }));
      });
  }, [resendLink, dispatch, setNotificationError]);

  const getBtnText = (): string => {
    if (blockedButtonText) {
      return blockedButtonText;
    }
    if (isEmailButtonActive) {
      return __("Wyślij ponownie");
    }
    return __("Wysłano");
  };

  const content = getBtnText();

  return (
    <ActivityItem
      {...restProps}
      additionalElement={editEmailButton}
      onClick={onResendEmailHandler}
      disabled={!isEmailButtonActive}
    >
      {content}
    </ActivityItem>
  );
};

export default EmailSenderActivityItem;
